import { forwardRef, memo } from "react";

import { cn } from "@/components/podkit/lib/cn";

export const GetStartedWithTasksShowcase = forwardRef<HTMLDivElement>(
    function GetStartedWithTasksShowcase(_props, ref) {
        return (
            <div ref={ref} className="flex h-full w-full flex-col items-center justify-center p-4">
                <div className="mb-6 flex w-full max-w-lg select-none flex-col items-center justify-center space-y-1">
                    <ShowcaseListItem
                        title="Weekly digest"
                        description="Ready to review"
                        active
                        translateY={64}
                        scale={0.86}
                    />
                    <ShowcaseListItem
                        title="Add command palette"
                        description="Working…"
                        active={false}
                        translateY={32}
                        scale={0.93}
                    />
                    <ShowcaseListItem
                        title="Implement prompts API"
                        description="Starting environment…"
                        active={false}
                        translateY={0}
                        scale={1}
                    />
                </div>
                <p className="mb-2 text-2xl font-medium text-black dark:text-white lg:text-3xl">
                    Build the future while you sleep
                </p>
                <p className="max-w-lg text-center text-base text-content-secondary lg:text-lg">
                    Set up powerful{" "}
                    <a
                        href="https://ona.com/docs/ona/automations/overview"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-content-primary underline decoration-content-secondary/40 hover:decoration-content-primary"
                    >
                        automations
                    </a>{" "}
                    to complete software engineering activities, run reports and fix bugs at any scale.
                </p>
                <p className="mt-3 max-w-lg text-center text-base font-medium text-black dark:text-white lg:text-lg">
                    All in the cloud while your laptop is switched off.
                </p>
            </div>
        );
    },
);

function ShowcaseListItem({
    title,
    description,
    active,
    translateY,
    scale,
}: {
    title: string;
    description: string;
    active: boolean;
    translateY: number;
    scale: number;
}) {
    return (
        <div
            className="flex w-full flex-col items-center justify-center rounded-[14px] bg-white/80 p-2 shadow-[0_0.5px_0.5px_0.5px_rgba(255,255,255,0.30)_inset,0_2px_16px_0_rgba(0,0,0,0.15)] backdrop-blur-xl dark:bg-[rgb(var(--ona-black))]/80"
            style={{
                transform: `translateY(${translateY}px) scale(${scale})`,
            }}
        >
            <div className="flex w-full flex-row items-center justify-start rounded-md bg-white p-4 dark:bg-[rgb(var(--ona-black))]">
                <div
                    className={cn(
                        "mr-3 flex size-9 items-center justify-center rounded-md",
                        active ? "bg-[#CEFFC9]" : "bg-[#FCEADE]",
                    )}
                >
                    {active ? <ActiveDot /> : <InactiveDot />}
                </div>
                <div className="flex flex-col">
                    <p className="text-[rgb(var(--ona-gray-900))] dark:text-[rgb(var(--ona-gray-100))]">{title}</p>
                    <p className="text-base text-[rgb(var(--ona-gray-500))] dark:text-[rgb(var(--ona-gray-400))]">
                        {description}
                    </p>
                </div>
            </div>
        </div>
    );
}

const ActiveDot = memo(() => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="27" viewBox="0 0 28 27" fill="none">
            <circle cx="13.8114" cy="13.309" r="7.81872" fill="#A7F7A1" />
            <circle cx="13.8116" cy="13.3113" r="2.60624" fill="#0BD300" />
        </svg>
    );
});
ActiveDot.displayName = "ActiveDot";

const InactiveDot = memo(() => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
            <circle cx="14.5743" cy="14.0681" r="8.52127" fill="#FFDAC3" />
            <circle cx="14.5743" cy="14.0689" r="2.84043" fill="#EE6002" />
        </svg>
    );
});
InactiveDot.displayName = "InactiveDot";
